import * as React from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import { visuallyHidden } from "@mui/utils";
import { TableDataFormationEnum } from "../models/company.model";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import { Badge, Button, IconButton, Menu, MenuItem, Stack, Tooltip } from "@mui/material";
import LoadingButton from "../components/LoadingButton";
import { downloadDataIntoCSV, titleStr } from "../lib/helper";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FormHierarchySelection from "../components/Form/FormHierarchySelection";
import { useForm } from "react-hook-form";
import { useRef } from "react";
import { useConfig } from "./ConfigContext";
import { ReportModel } from "../models/report.model";

function descendingComparator(a: any, b: any, orderBy: string) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

type Order = "asc" | "desc";

function getComparator<T>(order: Order, orderBy: string): (a: T, b: T) => number {
  return order === "desc" ? (a, b) => descendingComparator(a, b, orderBy) : (a, b) => -descendingComparator(a, b, orderBy);
}

interface HeadCell {
  id: string;
  label: string;
  dataFormation?: TableDataFormationEnum;
  align?: string;
  decimalPlaces?: number;
}

interface EnhancedTableProps {
  onRequestSort: (event: React.MouseEvent<unknown>, property: string) => void;
  order: Order;
  orderBy: string;
  headCells: HeadCell[];
  groupBy: string;
  filter?: string[];
  setFilter: any;
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const { order, orderBy, onRequestSort, headCells } = props;
  const createSortHandler = (property: string) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property);
  };
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const filterFormRef = useRef<any>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <TableHead>
      <TableRow>
        <TableCell sx={{ borderBottom: "none", padding: 0, width: "1px" }}>
          <Tooltip title="Filter">
            <IconButton onClick={handleClick}>
              <Badge color="error" variant="dot" invisible={!props.filter}>
                <FilterAltIcon />
              </Badge>
            </IconButton>
          </Tooltip>
          <Menu id="basic-menu" anchorEl={anchorEl} open={open} onClose={handleClose}>
            <MenuItem
              disableRipple
              sx={{
                "&:hover": {
                  backgroundColor: "transparent",
                },
              }}
            >
              <FilterForm
                ref={filterFormRef}
                groupBy={props.groupBy}
                filter={props.filter}
                setFilter={props.setFilter}
                onCloseMenu={handleClose}
              />
            </MenuItem>
          </Menu>
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={(headCell.align || "center") as any}
            padding={"normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

const FilterForm = React.forwardRef((props: { groupBy: string; filter?: string[]; setFilter: any; onCloseMenu: any }, ref: any) => {
  const { control, setError, handleSubmit } = useForm({
    defaultValues: {
      hierarchies: props.filter,
    },
  });

  const onReset = () => {
    handleSubmit((data) => {
      props.setFilter(undefined);
      props.onCloseMenu();
    })();
  };

  const onApply = () => {
    handleSubmit((data) => {
      props.setFilter(data.hierarchies);
      props.onCloseMenu();
    })();
  };

  return (
    <Stack ref={ref} style={{ minWidth: 300, margin: 16 }} spacing={4}>
      <FormHierarchySelection
        name="hierarchies"
        multiple
        control={control}
        setError={setError}
        label="Hierarchy Filter"
        displayType={props.groupBy as any}
      />
      <Stack direction={"row"} justifyContent={"space-between"}>
        <Button variant="outlined" onClick={onReset}>
          Reset
        </Button>
        <Button variant="contained" onClick={onApply}>
          Apply
        </Button>
      </Stack>
    </Stack>
  );
});

export function GroupSortTable(props: { headCells: HeadCell[]; rows: ReportModel[]; groupBy: string; storeLabelTypes?: string[] }) {
  const { headCells, rows, groupBy } = props;
  const [order, setOrder] = React.useState<Order>("asc");
  const [orderBy, setOrderBy] = React.useState<string>("groupLabel");
  const [page, setPage] = React.useState(0);
  const [dense] = React.useState(false);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [filter, setFilter] = React.useState<string[] | undefined>(undefined);
  const config = useConfig();
  const companyId = config.loggedUser?.companyId || "";
  const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredRows = React.useMemo(
    () => rows.filter((r) => !filter || filter.includes(companyId) || filter?.map((f) => f.toLowerCase()).includes(r.id)),
    [rows, filter]
  );

  const visibleRows = React.useMemo(
    () => [...filteredRows].sort(getComparator(order, orderBy)).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [order, orderBy, page, rowsPerPage, filteredRows]
  );

  const onDownload = async () => {
    const groupLabelHead = props.headCells.find((h) => h.id === "groupLabel");
    const storeLabelTypes = props.storeLabelTypes || [];
    const splitGroupLabels = storeLabelTypes.length > 0 ? storeLabelTypes.sort() : [groupLabelHead?.label || "header"];
    const csv: string[][] = [
      [...splitGroupLabels.map((l) => titleStr(l)), ...props.headCells.filter((h) => h.id !== groupLabelHead?.id).map((h) => h.label)],
    ];
    [...filteredRows].sort(getComparator(order, orderBy)).forEach((row: any, index: number) => {
      csv[index + 1] = headCells.map((h) => row[h.id]);
    });
    downloadDataIntoCSV(csv.map((row) => row.join(",")).join("\n"), `Grouped Report`);
  };

  const formatValue = (value: unknown, decimalPlaces: number) => {
    if (typeof value === 'number') {
      return value.toFixed(decimalPlaces);
    }
    return String(value);
  }

  return (
    <Box sx={{ width: "100%", height: "100%" }} key={page}>
      <TableContainer sx={{ height: "88%" }}>
        <Table stickyHeader aria-labelledby="tableTitle" size={dense ? "small" : "medium"}>
          <EnhancedTableHead
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            headCells={headCells}
            groupBy={groupBy}
            filter={filter}
            setFilter={setFilter}
          />
          <TableBody>
            {visibleRows.map((row) => {
              return (
                <TableRow tabIndex={-1} key={row.id} sx={{ cursor: "pointer" }}>
                  <TableCell sx={{ borderBottom: "none" }}></TableCell>
                  {headCells.map((headCell, colIndex) => {
                    const value = row[headCell.id] as any;
                    const decimalPlaces = headCell.decimalPlaces || 0;
                    const colLeft = row?.[headCells[colIndex - 1]?.id] || 0;
                    return (
                      <TableCell align={(headCell.align || "center") as any} key={`${row.id}${headCell.id}`}>
                        {!headCell.dataFormation || headCell.dataFormation === TableDataFormationEnum.Default ? (
                          <div>{formatValue(value, decimalPlaces)}</div>
                        ) : headCell.dataFormation === TableDataFormationEnum.Percentage ? (
                          <div>{formatValue(value, decimalPlaces)}%</div>
                        ) : Math.abs(value - colLeft) < 0.0001 ? (
                          <TrendingFlatIcon color="warning" />
                        ) : value < colLeft ? (
                          <TrendingDownIcon color="error" />
                        ) : (
                          <TrendingUpIcon color="success" />
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack direction={"row"} justifyContent={"space-between"}>
        <LoadingButton variant="text" onClick={onDownload}>
          Download
        </LoadingButton>
        <TablePagination
          sx={{
            height: "12%",
            overflow: "hidden",
            "& .MuiToolbar-root": {
              paddingTop: 0,
              paddingBottom: 0,
              height: "100%",
              minHeight: 32,
            },
          }}
          rowsPerPageOptions={[10, 20, 50]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Stack>
    </Box>
  );
}
